import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import { StyleSheet, Text, View } from "react-native";
import {
  BG_STYLES,
  Button,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  LazyImage,
  SIZE,
  Spinner,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import AddCropModal from "../components/crop/AddCropModal";
import { CalendarGrid } from "../components/Calender";
import {
  DIMENSION_WIDTH,
  SCREEN_HEIGHT,
  withAsyncCooldown,
} from "app/utils/common";
import createSubscription from "../apis/createSubscription";
import { AdvisoryResponse, Crop, SubScribedCrops } from "../models/crop";
import images from "../assets/image";
import SuccessIcon from "../components/icons/SuccessIcon";
import { AxiosError } from "axios";
import RoundImageWithIcon from "../components/reusable/RoundImageWithIcon";
import { getCurrentLocationValues } from "../utils/google";
import { extractCropAndSowingDate } from "../utils/helpers";
import BottomDrawer from "../components/reusable/BottomDrawer";
import useErrorModalState from "../hooks/useErrorModalState";
import ErrorModal from "../components/reusable/ErrorModal";
import Okay from "../components/reusable/Okay";
import { fetchUserDetails } from "../apis/fetchUserDetails";
import User from "../models/user";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import ToastAndroid from "app/mocks/ToastAndroid";
import Crops from "../components/crop";
import fetchSubscribedCorps from "../apis/fetchSubscribedCrops";
import { useTranslation } from "react-i18next";
import getClientCode from "app/farmer360/utils/helpers/getClientCode";
import { getCookieValue } from "app/farmer360/utils/helper";
import { APP_CODE_TYPE } from "app/farmer360/models/auth";
import { NAVIGATION_FROM_APP_CODE } from "app/farmer360/constants/common";
import { KhetEdgeScreenProps } from "../routes/types";

interface FormData {
  crop: Crop | null;
  sowingDate: string;
  latLong: string;
  isTransplanted?: boolean;
}
const KhetEdge = ({ navigation }: KhetEdgeScreenProps) => {
  const { t } = useTranslation();
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>({
    crop: null,
    sowingDate: "",
    latLong: "",
    isTransplanted: false,
  });
  const [isloading, setIsLoading] = useState(true);
  const { errorModalState, setErrorModalState, hideErrorModal } =
    useErrorModalState();

  const [advisoryData, setAdvisoryData] = useState<AdvisoryResponse | null>(
    null
  );
  const [subscribed, setSubscribed] = useState(false);
  const [isSubscribedLoading, setIsSubscribedLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [crops, setCrops] = useState<SubScribedCrops[]>([]);
  const [isCropsloading, setIsCropsLoading] = useState(false);

  const clientCode = getClientCode(
    getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE
  );

  const { cropImage, cropName, sowingDate, cropId } =
    extractCropAndSowingDate(advisoryData);

  const handleSubscribe = useCallback(
    async (data: FormData) => {
      try {
        const { crop, latLong, sowingDate, isTransplanted } = data;
        const sowingDateId = isTransplanted ? 11 : 2;
        const payload = {
          plan_id: 1,
          data_queries: [
            {
              id: 1,
              value: crop?.id,
            },
            {
              id: 3,
              value: latLong,
            },
            {
              id: sowingDateId,
              value: sowingDate,
            },
          ],
        };
        const res = await createSubscription(payload);
        setAdvisoryData(res);
        setSubscribed(true);
        trackEvent(CustomEventTypes.AdvisorySubscriptionCreated, {
          farmerName: userDetails?.full_name,
          farmerMobile: userDetails?.phone_number,
          AdvisoryType: "KhetEdge",
          CropName: crop?.name || "",
          DateOfSowing: sowingDate,
          LatLong: latLong,
        });
        getCrops();
        setIsSubscribedLoading(false);
      } catch (error) {
        if (error instanceof AxiosError) {
          const data = error?.response?.data;

          const errorMessage =
            data?.data_queries?.[0] ||
            data?.error?.message ||
            data?.message ||
            data?.detail ||
            error?.message;
          setErrorModalState({
            show: true,
            message: errorMessage,
            actionNode: (
              <Okay
                onClick={() => hideErrorModal()}
                btnStyle={COMMON_STYLES.mt1}
              />
            ),
          });
        }
        setIsSubscribedLoading(false);
      }
    },
    [
      hideErrorModal,
      setErrorModalState,
      userDetails?.full_name,
      userDetails?.phone_number,
    ]
  );

  const handleAddLocation = useCallback(async () => {
    if (isSubscribedLoading) return;
    setIsSubscribedLoading(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const latLong = `${position.coords.latitude},${position.coords.longitude}`;
          setFormData((prev) => ({ ...prev, latLong }));
          trackEvent(CustomEventTypes.AdvisorySubscriptionLocationAdded, {
            farmerName: userDetails?.full_name,
            farmerMobile: userDetails?.phone_number,
            AdvisoryType: "KhetEdge",
            CropName: formData?.crop?.name || "",
            DateOfSowing: formData.sowingDate,
            LatLong: latLong,
          });
          await handleSubscribe({ ...formData, latLong });
        },
        (error) => {
          setIsSubscribedLoading(false);
          setErrorModalState({
            show: true,
            message: "Unable to fetch location",
            actionNode: (
              <Okay
                onClick={() => hideErrorModal()}
                btnStyle={COMMON_STYLES.mt1}
              />
            ),
          });
        }
      );
    } else {
      setErrorModalState({
        show: true,
        message: "Allow location access",
        actionNode: (
          <Okay onClick={() => hideErrorModal()} btnStyle={COMMON_STYLES.mt1} />
        ),
      });
    }
  }, [
    formData,
    handleSubscribe,
    hideErrorModal,
    isSubscribedLoading,
    setErrorModalState,
    userDetails?.full_name,
    userDetails?.phone_number,
  ]);

  const handleAddLocationThrottle = useMemo(
    () => withAsyncCooldown(handleAddLocation),
    [handleAddLocation]
  );

  const getCrops = async () => {
    try {
      setIsCropsLoading(true);
      const crops = await fetchSubscribedCorps();
      setCrops(crops);
    } catch (error) {
      if (error instanceof AxiosError) {
        const data = error?.response?.data;
        const errorMessage =
          data?.data_queries?.[0] ||
          data?.error?.message ||
          data?.message ||
          data?.detail ||
          error?.message;
        ToastAndroid.show(
          errorMessage || "Something went wrong",
          ToastAndroid.SHORT
        );
      }
    }
    setIsCropsLoading(false);
  };

  useEffect(() => {
    const getUserDetails = async () => {
      setIsLoading(true);
      try {
        let user: User | null;
        user = await fetchUserDetails();
        if (user) {
          setUserDetails(user);
        }
      } catch (err) {
        ToastAndroid.show("Unable to fetch user details", ToastAndroid.SHORT);
      }
      setIsLoading(false);
    };
    getUserDetails();
  }, []);

  useEffect(() => {
    getCrops();
    if (userDetails?.id) {
      trackEvent(CustomEventTypes.LandedonKhetEdgeHome, {
        userName: userDetails?.full_name,
        userMobile: userDetails?.phone_number,
        farmerName: userDetails?.full_name,
        farmerMobile: userDetails?.phone_number,
        AdvisoryType: "KhetEdge",
      });
    }
  }, [userDetails]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: clientCode !== "vr-service",
    });
  }, [clientCode, navigation]);

  if (isloading) {
    return <Spinner />;
  }

  return (
    <View style={styles.container}>
      {isSubscribedLoading ? <Spinner type="on-screen" /> : null}
      <Crops
        handleAddCrop={() => {
          setCurrentStep(1);
          trackEvent(CustomEventTypes.SubscriptionEnrolmentInitiated, {
            userName: userDetails?.full_name,
            userMobile: userDetails?.phone_number,
            farmerName: userDetails?.full_name,
            farmerMobile: userDetails?.phone_number,
            AdvisoryType: "KhetEdge",
          });
        }}
        crops={crops}
        loading={isCropsloading}
      />

      {currentStep === 1 ? (
        <AddCropModal
          onSubmit={(crop) => {
            if (crop?.id) {
              setFormData({
                ...formData,
                crop,
                isTransplanted: crop?.is_transplanted,
              });
              setCurrentStep(2);
              trackEvent(CustomEventTypes.AdvisorySubscriptionCropSelected, {
                farmerName: userDetails?.full_name,
                farmerMobile: userDetails?.phone_number,
                AdvisoryType: "KhetEdge",
                CropName: crop.name,
              });
            }
          }}
          onClose={() => setCurrentStep(0)}
        />
      ) : null}
      <BottomDrawer
        visible={currentStep === 2}
        onClose={() => setCurrentStep(0)}
        closeOnBackdropPress
        modalStyle={{
          height: DIMENSION_WIDTH > 600 ? undefined : SCREEN_HEIGHT - 10,
        }}
      >
        <View
          style={[
            COMMON_STYLES.flex1,
            COMMON_STYLES.mb1,
            COMMON_STYLES["mv1/2"],
          ]}
        >
          <Text style={[TYPOGRAPHY.HEADING2, VARIANTS_STYLES.MEDIUM]}>
            {formData?.isTransplanted
              ? t("add_transplanation_date")
              : t("add_sowing_date")}
          </Text>
          {formData.crop ? (
            <View style={[COMMON_STYLES["mv1/2"]]}>
              <RoundImageWithIcon
                crop={formData.crop}
                key={formData.crop.id}
                isIcon
                imageStyle={styles.image}
                imageContainerStyle={styles.iconContainer}
                containerStyle={[COMMON_STYLES.row]}
                isImageSelected={false}
              />
            </View>
          ) : null}
          <CalendarGrid
            onDateSelected={(dateStr) => {
              if (dateStr) {
                const dateObj = new Date(dateStr);

                const day = String(dateObj.getDate()).padStart(2, "0");
                const month = String(dateObj.getMonth() + 1).padStart(2, "0");
                const year = dateObj.getFullYear();

                const formattedDate = `${day}-${month}-${year}`;
                setFormData({ ...formData, sowingDate: formattedDate });
              }
            }}
          />
          <View style={styles.footer}>
            <Button
              type={BUTTON_TYPE.SECONDARY}
              onClick={() => {
                setCurrentStep(1);
                setFormData({ ...formData, sowingDate: "" });
              }}
              text={t("button_back")}
              size={SIZE.large}
              buttonStyle={[COMMON_STYLES.flex1]}
            />
            <Button
              onClick={() => {
                if (formData.sowingDate) {
                  setCurrentStep(3);
                  trackEvent(
                    CustomEventTypes.AdvisorySubscriptionDateOfSowingSelected,
                    {
                      farmerName: userDetails?.full_name,
                      farmerMobile: userDetails?.phone_number,
                      AdvisoryType: "KhetEdge",
                      CropName: formData?.crop?.name || "",
                      DateOfSowing: formData.sowingDate,
                    }
                  );
                }
              }}
              text={t("button_next")}
              size={SIZE.large}
              buttonStyle={[COMMON_STYLES.flex1]}
            />
          </View>
        </View>
      </BottomDrawer>
      <BottomDrawer
        visible={currentStep === 3}
        onClose={() => setCurrentStep(0)}
        closeOnBackdropPress
      >
        <View style={[COMMON_STYLES.pb1, { gap: 16 }]}>
          <View>
            <Text style={[TYPOGRAPHY.HEADING2, VARIANTS_STYLES.MEDIUM]}>
              {t("send_your_location")}
            </Text>
            <Text style={[TYPOGRAPHY.TEXT2, { color: COLORS.neutral80 }]}>
              {t("enable_location_description")}
            </Text>
          </View>

          <LazyImage
            imageUrl={images.LocationImage}
            imgStyles={[
              BG_STYLES.bgWhite,
              { height: 220, borderRadius: 8, resizeMode: "contain" },
            ]}
          />
          <Button
            size={SIZE.large}
            text={t("send_location")}
            onClick={handleAddLocationThrottle}
          />
        </View>
      </BottomDrawer>
      <BottomDrawer
        visible={subscribed}
        onClose={() => {
          setSubscribed(false);
          setCurrentStep(0);
        }}
      >
        <View style={[COMMON_STYLES.pb1]}>
          <View
            style={[
              BG_STYLES.bgPrimary20,
              COMMON_STYLES["p3/2"],
              COMMON_STYLES.justifyCenter,
              COMMON_STYLES.alignItemsCenter,
              { display: "flex", borderRadius: 16, gap: 16 },
            ]}
          >
            <SuccessIcon />
            <Text
              style={[
                TYPOGRAPHY.HEADING3,
                VARIANTS_STYLES.MEDIUM,
                { color: COLORS.primary100, textAlign: "center" },
              ]}
            >
              {t("subscription_success")}
            </Text>
          </View>
          <View
            style={[
              BG_STYLES.bgNeutral10,
              COMMON_STYLES["p3/2"],
              COMMON_STYLES.mt1,
              { borderRadius: 16, gap: 20 },
            ]}
          >
            <RoundImageWithIcon
              crop={{
                name: cropName || "",
                image_url: cropImage || "",
                id: cropId || 0,
              }}
              key={cropId || 0}
              isIcon
              imageStyle={styles.image}
              imageContainerStyle={styles.iconContainer}
              containerStyle={[COMMON_STYLES.row, COMMON_STYLES.justifyCenter]}
              isImageSelected={false}
            />

            <View>
              <View style={[COMMON_STYLES.row, COMMON_STYLES["mb0.5"]]}>
                <Text style={styles.text}>{t("sowing")}: </Text>{" "}
                <Text
                  style={[styles.text, VARIANTS_STYLES.MEDIUM, { flex: 0.6 }]}
                >
                  {sowingDate}
                </Text>
              </View>
            </View>
          </View>
          <Button
            text={t("okay")}
            onClick={() => {
              setCurrentStep(0);
              setSubscribed(false);
              setFormData({ crop: null, sowingDate: "", latLong: "" });
              setAdvisoryData(null);
            }}
            size={SIZE.large}
            buttonStyle={[COMMON_STYLES.mt1]}
          />
        </View>
      </BottomDrawer>
      <ErrorModal
        visible={errorModalState.show}
        onClose={hideErrorModal}
        message={errorModalState.message}
        allowClose={errorModalState.allowClose}
      >
        {errorModalState.actionNode}
      </ErrorModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "static",
    overflow: "scroll",
    height: "100%",
    width: "100%",
    display: "flex",
    flex: 1,
    gap: 4,
    backgroundColor: COLORS.white,
  },
  addCropContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginVertical: 16,
  },
  img100X100: {
    height: 50,
    width: 50,
    borderRadius: 50,
  },
  addCropBtn: {
    borderWidth: 1,
    borderColor: COLORS.primary100,
    backgroundColor: COLORS.primary20,
    alignItems: "center",
    justifyContent: "center",
  },
  "mr5/2": {
    marginRight: 40,
  },
  image: {
    height: 40,
    width: 40,
  },
  iconContainer: {
    borderWidth: 1,
    borderColor: COLORS.neutral20,
    marginRight: 10,
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 20,
    justifyContent: "space-between",
    gap: 20,
  },
  text: {
    flex: 0.4,
    color: COLORS.neutral90,
  },
});

export default KhetEdge;
